import { ButtonIcon, ButtonTertiary } from "@nova/component-btn"
import { FC, useState } from "react"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Divider } from "components/Divider"
import { navbarHeight } from "@nova/constants"
import { InfraredFlight } from "@nova/protobuf"
import { selectInfraredFlightById } from "features/entity-caching/flights/selectors"
import { closeHotspotPanel, toggleFieldOfView, toggleImageOverlay, toggleList } from "./slice"
import {
    selectHotspotPanelItems,
    selectHotspotPanelListOpen,
    selectHotspotPanelOverlayOptions,
} from "./selectors"
import { UserPlacedHotspots } from "plugins/InfraredImage/HotspotMenu/UserPlacedHotspots"
import { selectIsHotspotMultiRemovalOpen } from "features/data-management/hotspots/selectors"
import {
    closeHotspotMultiRemoval,
    openHotspotMultiRemoval,
} from "features/data-management/hotspots/slice"
import { useCursorTracking } from "tools/events/useCursorProvider"
import classNames from "classnames"
import { selectMergedFlightById } from "features/entity-caching/mergedFlights/selectors"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { ToggleSwitch } from "@nova/component-toggle-switch"

interface HotspotTopBarBodyProps {
    flight: InfraredFlight
}

const HotspotTopBarBody: FC<HotspotTopBarBodyProps> = ({ flight }) => {
    const dispatch = useAppDispatch()
    const mergedFlight = useAppSelector(selectMergedFlightById(flight.mergeId ?? ""))

    const overlayOptions = useAppSelector(selectHotspotPanelOverlayOptions)
    const hotspotMultiRemovalOpen = useAppSelector(selectIsHotspotMultiRemovalOpen)
    const listOpen = useAppSelector(selectHotspotPanelListOpen)
    const [isPlacingHotspot, setIsPlacingHotspot] = useState(false)
    const { isEditor } = useWorkspacePermissions()

    const handleClose = () => {
        dispatch(closeHotspotPanel())
    }

    const handleFieldOfViewClick = () => {
        dispatch(toggleFieldOfView())
    }

    const handleImageOverlayClick = () => {
        dispatch(toggleImageOverlay())
    }

    const handleShowListClick = () => {
        dispatch(toggleList())
    }

    const handleRemoveHotspotsClick = () => {
        setIsPlacingHotspot(false)
        if (hotspotMultiRemovalOpen) {
            dispatch(closeHotspotMultiRemoval())
        } else {
            dispatch(openHotspotMultiRemoval({ infraredFlightIds: flight.id }))
        }
    }

    const handlePlaceHotspotCick = () => {
        setIsPlacingHotspot((prev) => !prev)
    }

    return (
        <div className="flex justify-between text-gray-200">
            <div className="flex items-center justify-center gap-4">
                <ButtonIcon
                    Icon={Icons.XClose}
                    height={24}
                    width={24}
                    onClick={handleClose}
                    className="text-gray-300 hover:text-blue-500"
                />
                <Divider vertical />
                <div className="flex items-center gap-2 text-white-1000">
                    <Icons.Icon Icon={Icons.HotspotAddOn} />
                    <h4>{mergedFlight ? mergedFlight.name : flight.name}</h4>
                </div>
                <Divider vertical />
                <div className="flex items-center gap-2">
                    <small>Show List</small>
                    <ToggleSwitch isToggled={listOpen} setIsToggled={handleShowListClick} />
                </div>
                <Divider vertical />

                <div className="flex items-center gap-4">
                    <div className="flex gap-2">
                        <small>Field of View</small>
                        <ToggleSwitch
                            isToggled={overlayOptions.fieldOfView}
                            setIsToggled={handleFieldOfViewClick}
                        />
                    </div>

                    <div className="flex gap-2">
                        <small>Image Overlay</small>
                        <ToggleSwitch
                            isToggled={overlayOptions.imageOverlay}
                            setIsToggled={handleImageOverlayClick}
                        />
                    </div>
                </div>
            </div>
            {isEditor && (
                <div className="flex items-center gap-2">
                    <ButtonTertiary
                        title="Select Hotspots"
                        RightIcon={Icons.RemoveSelection}
                        onClick={handleRemoveHotspotsClick}
                        disabled={hotspotMultiRemovalOpen || isPlacingHotspot}
                    />
                    <ButtonTertiary
                        title="Place Hotspot"
                        RightIcon={Icons.Loupe}
                        onClick={handlePlaceHotspotCick}
                        className={classNames(isPlacingHotspot && "border-blue-500")}
                        disabled={hotspotMultiRemovalOpen}
                    />
                    {isPlacingHotspot && (
                        <UserPlacedHotspots
                            flightId={flight.id}
                            cancelHotspotPlace={() => setIsPlacingHotspot(false)}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

const hotspotTopBarId = "hotspot-panel-top-bar"

const HotspotTopBar = () => {
    const selected = useAppSelector(selectHotspotPanelItems)
    const flightId = selected.flightId ?? ""
    const flight = useAppSelector(selectInfraredFlightById(flightId))
    useCursorTracking(hotspotTopBarId)

    if (!flight) return <></>

    return (
        <div
            style={{ maxHeight: navbarHeight }}
            className="px-4 h-full content-center"
            id={hotspotTopBarId}
        >
            <HotspotTopBarBody flight={flight} />
        </div>
    )
}

export { HotspotTopBar }
